export interface OverflowableItem {
  id: string;
  priority: number; // Lower number = higher priority
  width: number;
  position: 'left' | 'middle' | 'right'; // Position in the overflow menu
}

const GAP = 8;

export type OverflowableItemWithVisibility = OverflowableItem & { visible: boolean };

function innerComputeVisibleElements(items: OverflowableItemWithVisibility[], containerWidth: number): OverflowableItemWithVisibility[] {
  const leftEnd = items.filter((item) => item.position === 'left' && item.visible).map((item) => item.width).reduce((a, b) => a + b + GAP, 0);
  const rightWidth = items.filter((item) => item.position === 'right' && item.visible).map((item) => item.width).reduce((a, b) => a + b + GAP, 0);
  const rightStart = containerWidth - rightWidth;
  const middleWidth = items.filter((item) => item.position === 'middle' && item.visible).map((item) => item.width).reduce((a, b) => a + b + GAP, 0);
  const middleStart = (containerWidth - middleWidth) / 2;
  const middleEnd = middleStart + middleWidth;

  let leftConflict = false;
  if (middleStart < leftEnd && middleWidth > 0 && leftEnd > 0) { leftConflict = true; }
  if (leftEnd > containerWidth) { leftConflict = true; }

  let rightConflict = false;
  if (rightStart < middleEnd && rightStart > 0 && middleEnd > 0) { rightConflict = true; }
  if (rightWidth > containerWidth) { rightConflict = true; }

  // If there are conflicts on both sides, find the lowest priority element and hide it
  if (leftConflict && rightConflict) {
    const lowestPriorityItem = items.reduce((prev, curr) => {
      if (!curr.visible) return prev;
      if (!prev || curr.priority > prev.priority) return curr;
      return prev;
    }, null as OverflowableItemWithVisibility | null);

    if (!lowestPriorityItem) {
      return items; // No visible items to hide
    }

    return innerComputeVisibleElements(
      items.map((item) => ({
        ...item,
        visible: item.visible && item.id !== lowestPriorityItem.id,
      })),
      containerWidth,
    );
  }

  // If there is only a conflict on the left side, hide the lowest priority item on the left or middle
  if (leftConflict) {
    const lowestPriorityItem = items.reduce((prev, curr) => {
      if (!curr.visible || curr.position === 'right') return prev;
      if (!prev || curr.priority > prev.priority) return curr;
      return prev;
    }, null as OverflowableItemWithVisibility | null);
    if (!lowestPriorityItem) {
      return items; // No visible items to hide
    }
    return innerComputeVisibleElements(
      items.map((item) => ({
        ...item,
        visible: item.visible && item.id !== lowestPriorityItem.id,
      })),
      containerWidth,
    );
  }

  // If there is only a conflict on the right side, hide the lowest priority item on the right or middle
  if (rightConflict) {
    const lowestPriorityItem = items.reduce((prev, curr) => {
      if (!curr.visible || curr.position === 'left') return prev;
      if (!prev || curr.priority > prev.priority) return curr;
      return prev;
    }, null as OverflowableItemWithVisibility | null);
    if (!lowestPriorityItem) {
      return items; // No visible items to hide
    }
    return innerComputeVisibleElements(
      items.map((item) => ({
        ...item,
        visible: item.visible && item.id !== lowestPriorityItem.id,
      })),
      containerWidth,
    );
  }

  // If no conflicts, return items as is
  return items;
}

export default function computeVisibleElements(items: OverflowableItem[], containerWidth: number): OverflowableItemWithVisibility[] {
  return innerComputeVisibleElements(
    items.map((item) => ({ ...item, visible: true })),
    containerWidth,
  );
}
